import { useState, useRef, useEffect, useCallback } from 'react';
import { APIWebSocketUrl, APIWebSocketToken } from '../../../Utils/Utils';
import { generateUniqueKey } from '../utils/generateUniqueKey';
import { MessageFormatter } from '../MessageFormatter.ts';

const useOrbitIntelChatSocket = ({ userId, userProfileData, deepInsightsRef, onResponse, onToggle, onLogs, setInputDisabled }) => {
  const socketRef = useRef(null);
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = generateUniqueKey();
  }
  const ignoreIncoming = useRef(false);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');

  // Callback refs — keep latest versions without re-running the socket effect
  const onResponseRef = useRef(onResponse);
  const onToggleRef = useRef(onToggle);
  const onLogsRef = useRef(onLogs);
  const setInputDisabledRef = useRef(setInputDisabled);

  useEffect(() => { onResponseRef.current = onResponse; });
  useEffect(() => { onToggleRef.current = onToggle; });
  useEffect(() => { onLogsRef.current = onLogs; });
  useEffect(() => { setInputDisabledRef.current = setInputDisabled; });

  const getUrl = () => `${APIWebSocketUrl()}/chat/web_chat?token=${APIWebSocketToken()}`;

  const handleSocketMessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      const type = (data.type || '').toLowerCase();

      if (ignoreIncoming.current && (type === 'logs' || type === 'response')) return;

      if (type === 'toggle') {
        onToggleRef.current();
      } else if (type === 'logs') {
        onLogsRef.current({ timestamp: data.timestamp, content: data.content });
      } else if (type === 'response') {
        const formatted = MessageFormatter.formatMessage(data.ai_response || '');
        onResponseRef.current(formatted);
        setInputDisabledRef.current(false);
      }
    } catch (err) {
      console.error('WS error parsing message:', err);
    }
  };

  useEffect(() => {
    if (!APIWebSocketUrl()) {
      setError('WebSocket URL is not configured.');
      return;
    }

    const socket = new WebSocket(getUrl());
    socketRef.current = socket;

    socket.onopen = () => {
      console.log('WS connected');
      setIsConnected(true);
      setError('');
      if (socket._queuedPayload) {
        socket.send(socket._queuedPayload);
        socket._queuedPayload = undefined;
      }
    };

    socket.onmessage = handleSocketMessage;

    socket.onerror = () => {
      setError('WebSocket connection error.');
      setIsConnected(false);
    };

    socket.onclose = () => {
      console.log('WS disconnected');
      setIsConnected(false);
    };

    const handleBeforeUnload = () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback((message) => {
    ignoreIncoming.current = false;

    const payload = JSON.stringify({
      user_details: {
        user_id: userId,
        user_email: userProfileData?.username,
        user_name: userProfileData?.firstName,
      },
      session_id: sessionIdRef.current,
      message,
      deep_insights: deepInsightsRef.current,
    });

    const current = socketRef.current;

    if (!current || current.readyState === WebSocket.CLOSED) {
      const socket = new WebSocket(getUrl());
      socket._queuedPayload = payload;
      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnected(true);
        setError('');
        if (socket._queuedPayload) {
          socket.send(socket._queuedPayload);
          socket._queuedPayload = undefined;
        }
      };
      socket.onmessage = handleSocketMessage;
      socket.onerror = () => {
        setError('WebSocket connection error.');
        setIsConnected(false);
      };
      socket.onclose = () => {
        console.log('WS disconnected');
        setIsConnected(false);
      };
      return;
    }

    if (current.readyState === WebSocket.CONNECTING) {
      current._queuedPayload = payload;
      return;
    }

    if (current.readyState === WebSocket.OPEN) {
      current.send(payload);
    }
  }, [userId, userProfileData, deepInsightsRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => {
    ignoreIncoming.current = true;
  }, []);

  const reconnect = useCallback(() => {
    sessionIdRef.current = generateUniqueKey();
    if (socketRef.current) {
      socketRef.current.close();
    }
    const socket = new WebSocket(getUrl());
    socketRef.current = socket;

    socket.onopen = () => {
      setIsConnected(true);
      setError('');
      if (socket._queuedPayload) {
        socket.send(socket._queuedPayload);
        socket._queuedPayload = undefined;
      }
    };
    socket.onmessage = handleSocketMessage;
    socket.onerror = () => {
      setError('WebSocket connection error.');
      setIsConnected(false);
    };
    socket.onclose = () => {
      console.log('WS disconnected');
      setIsConnected(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { sendMessage, stop, reconnect, sessionIdRef, isConnected, error };
};

export default useOrbitIntelChatSocket;
